import * as uuid from 'uuid';
import * as signer from 'signer-app/context/signer-app-client/signer';
import {
  Page,
  SignerField,
} from 'js/sign-components/generated/types/HelloRequest';
import page from 'signer-app/context/signer-app-client/page.png';
import { groupBy } from 'lodash';
import { MockUserName } from 'signer-app/signer-app-client/mock-signer-app-client';

const TestPage: Page = {
  src: page,
  width: 680,
  height: 880,
  orientation: 1,
  cdnSrc: null,
  documentId: uuid.v4(),
  snapshotGuid: uuid.v4(),
};

const TextField1: SignerField = {
  id: 'text-field-id',
  type: 'text',
  pageIndex: 0,
  x: 40,
  y: 40,
  width: 120,
  height: 20,
  documentId: TestPage.documentId,
  readOnly: false,
  required: true,
  name: 'First name',
  placeholder: 'enter something',
  fontSize: 12,
  originalFontSize: 12,
  fontFamily: 'arial',
  value: '',
  masked: false,
  validationType: 'letters_only',
};

const Checkbox: SignerField = {
  id: 'checkbox-id',
  type: 'checkbox',
  pageIndex: 0,
  name: 'Confirm',
  x: 340,
  y: 40,
  documentId: TestPage.documentId,
  readOnly: false,
  required: true,
  width: 20,
  height: 20,
};

const Initial: SignerField = {
  id: 'initial-id',
  type: 'initials',
  pageIndex: 0,
  name: 'Initials1',
  x: 40,
  y: 120,
  documentId: TestPage.documentId,
  readOnly: false,
  required: true,
  width: 120,
  height: 40,
};

const DateInput: SignerField = {
  id: 'date-input-id',
  type: 'date',
  pageIndex: 0,
  name: 'DateSigned1',
  dateFormat: 'MM / DD / YYYY',
  x: 320,
  y: 120,
  documentId: TestPage.documentId,
  readOnly: false,
  required: true,
  width: 120,
  height: 20,
};

const Dropdown: SignerField = {
  id: 'dropdown-id',
  type: 'dropdown',
  pageIndex: 0,
  name: 'Day of the week',
  x: 40,
  y: 220,
  documentId: TestPage.documentId,
  readOnly: false,
  required: true,
  width: 140,
  height: 20,
  options: [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ],
};

const Signature: SignerField = {
  id: 'signature-id',
  type: 'signature',
  pageIndex: 0,
  name: 'Signature1',
  x: 320,
  y: 220,
  documentId: TestPage.documentId,
  readOnly: false,
  required: true,
  width: 120,
  height: 40,
};

const RadioButton1: SignerField = {
  checked: false,
  documentId: TestPage.documentId,
  group: 'radio_group',
  groupLabel: 'Colors',
  height: 18,
  width: 18,
  id: 'radio-button-1-id',
  name: 'Red',
  pageIndex: 0,
  required: false,
  requirement: 'require_1',
  type: 'radiobutton',
  x: 480,
  y: 40,
};

const RadioButton2: SignerField = {
  checked: false,
  documentId: TestPage.documentId,
  group: 'radio_group',
  groupLabel: 'Colors',
  height: 18,
  width: 18,
  id: 'radio-button-2-id',
  name: 'Blue',
  pageIndex: 0,
  required: false,
  requirement: 'require_1',
  type: 'radiobutton',
  x: 480,
  y: 80,
};

export type SignerNamespace = typeof signer;

export default class MockSigner implements SignerNamespace {
  mockUserName: MockUserName;

  constructor(mockUserName: MockUserName) {
    this.mockUserName = mockUserName;
  }

  async loadData(cachedParamsToken?: string): Promise<signer.LegacySignerData> {
    switch (cachedParamsToken) {
      default:
        return {
          dpi: 80,
          pages: [TestPage],
          fields: [
            TextField1,
            RadioButton1,
            Initial,
            RadioButton2,
            DateInput,
            Dropdown,
            Checkbox,
            Signature,
          ],
          rules: [],
        };

      case 'Has signed data':
        return {
          dpi: 80,
          pages: [TestPage],
          fields: [
            TextField1,
            RadioButton1,
            Initial,
            RadioButton2,
            Dropdown,
            Checkbox,
            Signature,
          ],
          rules: [],
          signedData: {
            [TextField1.name!]: {
              type: 'inputtextbox',
              data: {
                value: 'any name',
              },
            },
            [RadioButton1.name!]: {
              type: 'inputradiobutton',
              data: {
                checked: true,
              },
            },
            [Initial.name!]: {
              type: 'inputinitials',
              data: {
                guid: 'anyguid',
              },
            },
            [Dropdown.name!]: {
              type: 'inputdropdown',
              data: {
                value: 'Friday',
              },
            },
            [Checkbox.name!]: {
              type: 'inputcheckmark',
              data: {
                checked: true,
              },
            },
            [Signature.name!]: {
              type: 'inputsignature',
              data: {
                guid: 'anyguid',
              },
            },
          },
        };

      case 'Mismatched fields':
        return {
          dpi: 80,
          pages: [TestPage],
          fields: [TextField1, RadioButton1],
          rules: [],
          signedData: {
            unknownName: {
              // unknown input
              type: 'inputtextbox',
              data: {
                value: 'any name',
              },
            },
            [RadioButton1.name!]: {
              type: 'inputcheckmark', // different type
              data: {
                checked: true,
              },
            },
          },
        };
    }
  }

  async loadUnifiedData(): Promise<any> {
    throw new Error('Not implemented');
  }

  async ready(
    _params: signer.LegacySignerReadyRequest,
  ): Promise<signer.LegacySignerReadyResponse> {
    return {
      success: true,
      error: null,
      data: {
        ready: true,
        error: false,
      },
    };
  }

  /**
   * This function takes the mocked response from `loadData` call and creates the
   * mock response that should be returned for the `/load` call. These endpoints
   * return related data, so the `id` and other property values need to match up
   * between the two responses. The `/load` call is not implemented in `appActions`.
   */
  async load(
    _params: signer.LegacySignerLoadRequest,
  ): Promise<signer.LegacySignerLoadResponse> {
    const mockedGetDataResponse = await this.loadData(this.mockUserName);
    const signer = {
      name: 'Test Signer',
      emailAddress: 'test@example.com',
      index: 1,
      cacheKey: uuid.v4(),
    };

    // Take the `pages` from the `getData` call and transform it into the
    // `documents` map returned by the `load` call
    const documentsList = mockedGetDataResponse.pages.map((page, pageIndex) => {
      // The `getData` response returns a flat list of fields, but the `load`
      // response has grouped fields.
      const flatFields = mockedGetDataResponse.fields
        .filter((field) => field.pageIndex === pageIndex)

        // Take the field that we would return for `getData` and convert to
        // what's returned for `load`. There is some redundant data here and
        // I'm not sure if everything is used.
        //
        // TODO: Is there a type def somewhere to use here?
        .map((field, idx) => {
          return {
            // These seems to be another `id` value. Not sure if it's used
            id: uuid.v4(),
            apiId: field.id ?? uuid.v4(),
            required: field.required ?? false,
            signer: signer.index,
            editable: true,
            fontFamily: field.fontFamily,
            fontSize: field.fontSize ?? 12,
            height: field.height ?? 14,
            isAutoFill: false,
            name: field.name,
            order: idx,
            originalFontSize: field.originalFontSize,
            pageNum: pageIndex + 1,
            type: field.type ?? 'text',
            value: field.value,
            width: field.width ?? 72,
            z: 0,
            conversionType: '',
            docNum: field.pageIndex ?? 1,
            group: field.group,
            validationType: field.validationType,
          };
        });

      // Create the necessary `group` fields from the flat list above
      const ungroupedId = uuid.v4();
      const { [ungroupedId]: unGrouped = [], ...groups } = groupBy(
        flatFields,
        (field) => field.group ?? ungroupedId,
      );

      const fields = [
        ...unGrouped,
        ...Object.values(groups).map((children) => ({
          type: 'group',
          required: true,
          children,
        })),
      ];

      return {
        guid: page.documentId,
        snapshot: uuid.v4(),
        pages: [],
        fields,
      };
    });

    const documents = Object.fromEntries(
      documentsList.map((doc) => [doc.guid, doc]),
    );

    return {
      success: true,
      error: null,
      data: {
        ready: true,
        signatureRequestData: {
          id: uuid.v4(),
          title: 'Test Signature Request',
          isTest: false,
          embeddedTypeCode: null,
          transmissionGuid: uuid.v4(),
          hasMixedFormat: false,
          allowColorSignature: false,
          isComingFromSmsDelivery: false,
          signer_options: {},
          signingRedirectUrl: null,
          xOffset: -100,
          isQualifiedSignature: false,
          documents,
          settings: {
            allowedSignatureTypes: {
              C: true,
              T: true,
              U: true,
              E: true,
              S: true,
            },
            defaultSignatureType: '',
            dateFormat: 'MM/DD/YYYY',
            cachedParamsToken: this.mockUserName,
            hasForm: false,
          },
          signer,
        },
      },
    };
  }

  async save(
    _params: signer.LegacySignerSaveRequest,
  ): Promise<signer.LegacySignerSaveResponse> {
    return {
      data: null,
      error: null,
      success: true,
    };
  }

  async finalize(
    _params: signer.LegacySignerFinalizeRequest,
  ): Promise<signer.LegacySignerFinalizeResponse> {
    return {
      success: true,
      error: null,
      data: {
        is_trusted: true,
        is_valid: true,
      },
    };
  }
}
